/**
 * Mirror of `tools/op-emulator/src/lib/endpoint-specs.ts` types.
 *
 * Kept manually in sync with the backend (Day 2 — frontend is too small
 * to justify a generated-types pipeline). Backend remains the SSoT; this
 * file is consumed only for typing the `/api/endpoints` response.
 *
 * When fields change in backend `endpoint-specs.ts`, update this file
 * AND the frontend components that consume them — typecheck will catch
 * any drift on the frontend side.
 */

export type HttpMethod = 'GET' | 'POST' | 'DELETE';

export type EndpointCategory = 'ops' | 'storage' | 'contract' | 'token' | 'subscriptions';

export type EndpointAuth = 'none' | 'op' | 'op+issuer';

export type EndpointBodyShape = 'none' | 'json' | 'multipart' | 'binary-response';

export type SignedSpecKind = 'case' | 'contract' | 'act' | null;

export interface PathParamSpec {
  name: string;
  defaultValue: string;
  hint: string;
}

export interface EndpointSpec {
  id: string;
  method: HttpMethod;
  pathTemplate: string;
  pathParams: PathParamSpec[];
  category: EndpointCategory;
  auth: EndpointAuth;
  bodyShape: EndpointBodyShape;
  signedSpec: SignedSpecKind;
  summary: string;
  defaultBody: unknown;
  renderOverride: boolean;
  notes?: string;
}

export interface EndpointCatalogResponse {
  count: number;
  endpoints: EndpointSpec[];
}

export interface ProxyOk {
  proxy_status: 'ok';
  endpoint_id: string;
  gateway_url: string;
  gateway_method: string;
  gateway_status_code: number;
  gateway_body: unknown;
  signing_applied: SignedSpecKind;
  idempotency_key?: string;
  /**
   * `true` when Gateway sent `idempotent-replayed: true` — request
   * was hashed-equal to a prior one within the 24h cache; handler was
   * NOT re-executed. See backend `routes/proxy.ts:ProxyOk` for source
   * detail. Omitted when the header was absent.
   */
  idempotent_replayed?: true;
  elapsed_ms: number;
}

export interface ProxyErr {
  proxy_status: 'gateway_unreachable' | 'bad_request';
  endpoint_id: string;
  gateway_url: string;
  gateway_method: string;
  error_kind: 'network' | 'timeout' | 'malformed_json' | 'validation' | 'unknown_endpoint';
  error_message: string;
  elapsed_ms: number;
}

export type ProxyResult = ProxyOk | ProxyErr;

export function isProxyOk(r: ProxyResult): r is ProxyOk {
  return r.proxy_status === 'ok';
}

/**
 * Mirror of `tools/op-emulator/src/webhook-bridge.ts:WebhookEntry`
 * (Day 3). Same one-source-of-truth-on-backend pattern as the rest of
 * this file. SSE frames + the /api/webhooks/snapshot JSON envelope
 * both ship objects of this shape.
 */
export interface WebhookEntry {
  id: number;
  receivedAt: string;
  rawBody: string;
  parsedBody: unknown;
  eventType: string | null;
  eventId: string | null;
  receivedSignature: string | null;
  expectedSignature: string | null;
  hmacValid: boolean | null;
  inspectedHeaders: Record<string, string>;
  /**
   * Replay-defense verdict (Phase 1.1):
   *   - `false` — first time we see this `event_id`, OR `event_id` was
   *               null (cannot dedup, treated as "fresh").
   *   - `true`  — a previous entry had the same non-null `event_id`.
   *               Duplicate is STILL rendered in the timeline (forensic
   *               value); UI surfaces a "replay" badge + link to the
   *               original entry via `firstSeenEntryId`.
   * See `tools/op-emulator/src/webhook-bridge.ts:WebhookEntry.duplicate`.
   */
  duplicate: boolean;
  /**
   * `id` of the first entry that saw this `event_id`, or `null` when
   * `duplicate === false`. Used by the timeline UI to render a
   * "↵ first seen as #N" reference next to the replay badge.
   */
  firstSeenEntryId: number | null;
}

/**
 * Response shape for `POST /api/webhooks/hmac-validate`. The
 * standalone validator returns the same `hmacValid` semantics as
 * `WebhookEntry.hmacValid` (null = no secret / true / false).
 */
export interface HmacValidateResponse {
  hmacValid: boolean | null;
  expectedSignature: string | null;
  receivedSignature: string | null;
  errorKind?: 'missing_field' | 'invalid_field_type';
  errorMessage?: string;
}

// ── Scenario Runner (Day 4) ─────────────────────────────────────

/**
 * Mirror of `tools/op-emulator/src/scenarios/types.ts:StepEvent`.
 * SSE frames `step.started` + `step.completed` ship objects of this
 * shape. UI renders one row per step in the Scenario Runner page.
 */
export type StepStatus = 'queued' | 'running' | 'passed' | 'failed' | 'skipped';

export interface ScenarioStepEvent {
  step_index: number;
  name: string;
  group: 'preflight' | 'business' | 'cleanup';
  status: StepStatus;
  result?: ProxyResult;
  expects_error_code?: string;
  note?: string;
  failure_reason?: string;
  elapsed_ms?: number;
}

export interface ScenarioStartedEvent {
  scenario_id: string;
  scenario_name: string;
  started_at: string;
  total_steps: number;
}

export interface ScenarioCompletedEvent {
  scenario_id: string;
  status: 'passed' | 'failed';
  finished_at: string;
  elapsed_ms: number;
  passed_count: number;
  failed_count: number;
  skipped_count: number;
}

export interface ScenarioMeta {
  id: string;
  name: string;
  group: 'happy-path' | 'error-injection';
  summary: string;
  approximate_steps: number;
  notes?: string;
}

export interface ScenarioCatalogResponse {
  count: number;
  scenarios: ScenarioMeta[];
}
